// components/dashboard/AttributionHero.jsx
// Live dashboard hero: the real attributed-revenue number (#68/#69) builds up
// before the merchant's eyes, out of a flowing timeline of the REAL emails sent
// and the REAL repeat purchases that followed.
//
// Honest by construction:
//  - The number that counts up IS summary.total_attributed_revenue (refund-aware,
//    correlation) — not the store total, not a fabricated figure.
//  - Emails and repeat purchases are two SEPARATE tracks on a shared time axis
//    (emails by send day, purchases by real purchase day) — no causal arrow.
//  - Every dot is a real day of activity; empty days are gaps, never filler.
//  - The #69 caption, ⓘ correlation caveat, chips, gated + empty states are kept.
//  - Respects prefers-reduced-motion: static number, static dots, no sweep.

import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { DollarSign, ArrowRight, Mail, ShoppingBag, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { safeApi, isFeatureError } from "@/utils/api";

export default function AttributionHero({ period = "30d" }) {
  const [data, setData] = useState(null);       // /attribution summary (authoritative number)
  const [timeline, setTimeline] = useState(null); // /attribution/timeline (flow series)
  const [gated, setGated] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const reduce = usePrefersReducedMotion();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // Timeline is enhancement-only: if it fails, the honest number still renders.
        const [attrRes, tlRes] = await Promise.all([
          safeApi.get(`/attribution?period=${period}`),
          safeApi.get(`/attribution/timeline?period=${period}`).catch(() => null),
        ]);
        if (!active) return;
        if (isFeatureError(attrRes)) {
          setGated(true);
          setData(null);
        } else {
          setGated(false);
          setData(attrRes?.data || attrRes);
          setTimeline(tlRes && !isFeatureError(tlRes) ? (tlRes?.data || tlRes) : null);
        }
      } catch (e) {
        // Keep whatever we had; never blank the number on a refetch error.
      } finally {
        if (active) setInitialLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [period]);

  if (initialLoading && !data) return <HeroSkeleton />;
  if (gated) return <HeroGated />;

  const summary = data?.summary || {};
  const windowHours = data?.attribution_window_hours ?? summary?.attribution_window_hours ?? 168;
  const windowDays = Math.round(windowHours / 24);
  const revenue = Number(summary.total_attributed_revenue || 0);
  const orders = Number(summary.total_attributed_orders || 0);
  const messagesSent = Number(summary.total_messages_sent || 0);
  const hasData = revenue > 0 || orders > 0;

  const series = Array.isArray(timeline?.series) ? timeline.series : null;
  const timelineHasActivity =
    !!series && series.some((d) => d.emails_sent > 0 || d.repeat_orders > 0);

  return (
    <Card className="overflow-hidden border-emerald-100">
      <div className="bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            {/* Hero number */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-emerald-700">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100">
                  <DollarSign className="w-4 h-4" />
                </span>
                <span className="text-sm font-medium">Revenue attributed to your emails</span>
                {/* Popover (not Tooltip) so the full honesty caveat opens on tap on mobile,
                    not just hover on desktop. */}
                <Popover>
                  <PopoverTrigger
                    aria-label="How this number is measured"
                    className="text-emerald-600/70 hover:text-emerald-700 focus:outline-none"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </PopoverTrigger>
                  <PopoverContent className="max-w-xs text-xs leading-relaxed text-gray-600">
                    Correlation, not causation: this counts orders placed within {windowDays} days
                    after an email was sent — it does not prove the email caused the purchase.
                    Refunded, voided and cancelled orders are excluded. Real Shopify orders only,
                    never estimates.
                  </PopoverContent>
                </Popover>
              </div>

              <p className="mt-3 text-4xl md:text-5xl font-bold text-gray-900 tracking-tight">
                <AnimatedRevenue value={revenue} animate={!reduce} />
              </p>

              <p className="mt-2 text-sm text-gray-500 leading-relaxed">
                {hasData ? (
                  <>Purchases made within {windowDays} days of your emails · refunds excluded</>
                ) : (
                  <>Your email-driven revenue will appear here as it comes in — real orders only, no estimates.</>
                )}
              </p>

              <Link
                to="/analytics?tab=attribution"
                className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-emerald-700 hover:text-emerald-800"
              >
                View full breakdown <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Supporting context — only when there's something real to show.
                Deliberately NO open-based stat here: open tracking is inflated
                by Apple MPP / Gmail proxies, and the hero must not lean on the
                exact signal Area 3 rejects. The engaged split (opened-or-
                clicked) still lives in the Analytics attribution tab, where it
                is labelled honestly and shown in context. */}
            {hasData && (
              <div className="grid grid-cols-2 md:flex md:flex-col gap-4 md:gap-3 md:min-w-[210px] md:border-l md:border-emerald-100 md:pl-6">
                <SupportStat icon={ShoppingBag} label="Attributed orders" value={orders.toLocaleString()} />
                <SupportStat icon={Mail} label="Emails sent" value={messagesSent.toLocaleString()} />
              </div>
            )}
          </div>

          {/* Live flow timeline — inside the same hero. Only when the endpoint returned. */}
          {series && (timelineHasActivity
            ? <FlowTimeline series={series} reduce={reduce} />
            : <EmptyFlowBand reduce={reduce} />)}
        </CardContent>
      </div>
    </Card>
  );
}

// ── Count-up: animates 0 → target once, easeOut, then holds. rAF, cleaned up on
//    unmount. On period change the component stays mounted, so it does NOT re-run
//    (per design: count-up once on load). Reduced motion → shows the final value.
function AnimatedRevenue({ value, animate }) {
  const [display, setDisplay] = useState(animate ? 0 : value);
  const startedRef = useRef(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!animate) {
      setDisplay(value);
      return;
    }
    if (startedRef.current) {
      // Already animated once (e.g. period changed) — just settle on the new value.
      setDisplay(value);
      return;
    }
    startedRef.current = true;
    const duration = 1400;
    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    let startTs = null;
    const tick = (ts) => {
      if (startTs === null) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      setDisplay(value * easeOutCubic(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(value);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, animate]);

  return <>${Math.round(display).toLocaleString()}</>;
}

// Count badge label — shown ONLY when there is more than one on a day.
// 1 → null (no "×1"); 2+ → "×N". Pure + exported-in-spirit for the node check.
function countBadge(n) {
  return n > 1 ? `×${n}` : null;
}

// Small pill rendered at the top-right of an icon when a day has more than one.
function CountPill({ label, color }) {
  return (
    <span
      className={`absolute -top-1.5 -right-2 h-[14px] min-w-[14px] px-1 rounded-full ${color} text-white text-[9px] font-semibold leading-[14px] text-center shadow-sm`}
    >
      {label}
    </span>
  );
}

// Track vertical positions (percent of the plot). Emails and purchases sit close
// together so a day reads as one column; both anchor down to the axis.
const EMAIL_TOP = 24;
const ORDER_TOP = 50;
const AXIS_TOP = 82;

// ── The flow: two separate tracks on a shared, period-aware time axis. Emails
//    (envelope, top) by send day; repeat purchases (bag, mid) by purchase day.
//    No connecting arrow — correlation only. Per day: ONE icon (+ a ×N badge when
//    there's more than one), vertically aligned and anchored to the axis. Icons
//    grow when data is sparse so a new merchant's few real events look intentional.
function FlowTimeline({ series, reduce }) {
  const n = series.length;
  const plotRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null); // { x, text }

  // Measure the plot so day columns can be laid out in px with a minimum gap.
  useLayoutEffect(() => {
    const el = plotRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => ro && ro.disconnect();
  }, []);

  const maxOrders = Math.max(1, ...series.map((d) => d.repeat_orders));
  const emailDays = series.map((d, i) => ({ d, i })).filter((o) => o.d.emails_sent > 0);
  const orderDays = series.map((d, i) => ({ d, i })).filter((o) => o.d.repeat_orders > 0);
  const activeIdx = [...new Set([...emailDays, ...orderDays].map((o) => o.i))].sort((a, b) => a - b);

  // Sparse → bigger icons; dense → smaller. Threshold ~8 total markers.
  const density = emailDays.length + orderDays.length;
  const base = density < 8 ? 24 : density < 20 ? 18 : density < 40 ? 14 : 11;
  const emailSize = Math.max(12, base - 3);

  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  // Axis ticks stay at TRUE time (percent of width); scale with the period.
  const xPctTrue = (i) => (n <= 1 ? 50 : 3 + (i / (n - 1)) * 94);
  const tickCount = n <= 1 ? 1 : Math.min(n, n <= 8 ? n : n <= 31 ? 6 : 7);
  const tickIdx =
    tickCount === 1
      ? [0]
      : Array.from({ length: tickCount }, (_, k) => Math.round((k / (tickCount - 1)) * (n - 1)));

  // Collision-free px positions for the active day columns: start from each day's
  // true time position, then enforce a minimum horizontal gap so adjacent active
  // days never overlap. If a cluster is too dense to fit, distribute evenly. The
  // icons drift slightly off exact-date-x (accepted), but the axis keeps true time.
  const colX = new Map();
  if (width > 0 && activeIdx.length) {
    const insetPx = width * 0.03;
    const leftBound = insetPx;
    const rightBound = width - insetPx;
    const minGap = base + 14;
    const xs = activeIdx.map((i) =>
      n <= 1 ? width / 2 : leftBound + (i / (n - 1)) * (rightBound - leftBound)
    );
    for (let k = 1; k < xs.length; k++) {
      if (xs[k] < xs[k - 1] + minGap) xs[k] = xs[k - 1] + minGap;
    }
    const overflow = xs[xs.length - 1] - rightBound;
    if (overflow > 0) for (let k = 0; k < xs.length; k++) xs[k] -= overflow;
    if (xs[0] < leftBound) {
      const step = xs.length > 1 ? (rightBound - leftBound) / (xs.length - 1) : 0;
      for (let k = 0; k < xs.length; k++) xs[k] = leftBound + k * step;
    }
    activeIdx.forEach((i, k) => colX.set(i, xs[k]));
  }

  const clampTip = (x) => Math.max(44, Math.min(width - 44, x));
  const ready = width > 0;

  return (
    <div className="mt-5 pt-4 border-t border-emerald-100/70">
      {/* legend + honest ordering note (correlation, and purchases follow emails) */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-2 text-[11px]">
        <span className="inline-flex items-center gap-1 text-indigo-500">
          <Mail size={12} /> Emails sent
        </span>
        <span className="inline-flex items-center gap-1 text-emerald-600">
          <ShoppingBag size={12} /> Repeat purchases
        </span>
        <span className="text-gray-400">· purchases always occur after the email</span>
      </div>

      {/* tooltip row — sits ABOVE the plot so it never covers the icons */}
      <div className="relative h-5 mb-1" aria-hidden="true">
        {ready && hover && (
          <span
            className="absolute -translate-x-1/2 whitespace-nowrap rounded bg-gray-900 text-white text-[10px] px-2 py-0.5 shadow-sm"
            style={{ left: clampTip(hover.x) }}
          >
            {hover.text}
          </span>
        )}
      </div>

      {/* plot */}
      <div ref={plotRef} className="relative h-24">
        {/* faint per-day connectors: anchor each active column down to the axis */}
        {ready &&
          activeIdx.map((i) => (
            <span
              key={`c-${i}`}
              className="absolute w-px bg-emerald-100"
              style={{ left: colX.get(i), top: `${EMAIL_TOP - 6}%`, height: `${AXIS_TOP - (EMAIL_TOP - 6)}%` }}
            />
          ))}

        {/* emails — top track (one envelope per day; ×N badge when >1) */}
        {ready &&
          emailDays.map(({ d, i }) => {
            const text = `${fmt(d.date)} · ${d.emails_sent} email${d.emails_sent !== 1 ? "s" : ""} sent`;
            return (
              <span
                key={`e-${d.date}`}
                className={`absolute text-indigo-400 ${reduce ? "" : "eco-pop"}`}
                aria-label={text}
                onMouseEnter={() => setHover({ x: colX.get(i), text })}
                onMouseLeave={() => setHover(null)}
                style={{
                  left: colX.get(i),
                  top: `${EMAIL_TOP}%`,
                  width: emailSize,
                  height: emailSize,
                  marginLeft: -emailSize / 2,
                  marginTop: -emailSize / 2,
                  animationDelay: `${Math.min(1.1, i * 0.03)}s`,
                }}
              >
                <Mail size={emailSize} strokeWidth={2.25} />
                {countBadge(d.emails_sent) && <CountPill label={countBadge(d.emails_sent)} color="bg-indigo-500" />}
              </span>
            );
          })}

        {/* repeat purchases — mid track, glowing (one bag per day; ×N badge when >1) */}
        {ready &&
          orderDays.map(({ d, i }) => {
            const size = Math.round(base + 6 * (d.repeat_orders / maxOrders));
            const text = `${fmt(d.date)} · ${d.repeat_orders} repeat purchase${d.repeat_orders !== 1 ? "s" : ""} · $${Math.round(d.repeat_revenue).toLocaleString()} · after your email`;
            return (
              <span
                key={`o-${d.date}`}
                className={`absolute text-emerald-600 ${reduce ? "" : "eco-pop"}`}
                aria-label={text}
                onMouseEnter={() => setHover({ x: colX.get(i), text })}
                onMouseLeave={() => setHover(null)}
                style={{
                  left: colX.get(i),
                  top: `${ORDER_TOP}%`,
                  width: size,
                  height: size,
                  marginLeft: -size / 2,
                  marginTop: -size / 2,
                  filter: "drop-shadow(0 0 5px rgba(16,185,129,0.55))",
                  animationDelay: `${Math.min(1.3, 0.2 + i * 0.03)}s`,
                }}
              >
                <ShoppingBag size={size} strokeWidth={2.25} />
                {countBadge(d.repeat_orders) && <CountPill label={countBadge(d.repeat_orders)} color="bg-emerald-600" />}
              </span>
            );
          })}

        {/* real time axis with period-aware ticks + date labels (TRUE time) */}
        <div className="absolute left-0 right-0" style={{ top: `${AXIS_TOP}%` }}>
          <div className="relative h-px bg-emerald-200">
            {tickIdx.map((i) => (
              <span
                key={`t-${i}`}
                className="absolute top-0 w-px h-1.5 bg-emerald-300"
                style={{ left: `${xPctTrue(i)}%` }}
              />
            ))}
          </div>
          <div className="relative mt-1.5 h-3">
            {tickIdx.map((i, k) => (
              <span
                key={`l-${i}`}
                className="absolute text-[10px] text-gray-400 whitespace-nowrap"
                style={{
                  left: `${xPctTrue(i)}%`,
                  transform:
                    k === 0
                      ? "translateX(0)"
                      : k === tickIdx.length - 1
                      ? "translateX(-100%)"
                      : "translateX(-50%)",
                }}
              >
                {fmt(series[i]?.date)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Inviting empty band: a decorative ambient sweep, NO data points or numbers.
function EmptyFlowBand({ reduce }) {
  return (
    <div className="mt-5 pt-4 border-t border-emerald-100/70">
      <div className="relative h-16 overflow-hidden">
        <div className="absolute left-0 right-0 top-1/2 border-t border-dashed border-emerald-200" />
        {!reduce && (
          <span
            className="eco-sweep absolute top-1/2 h-px w-16 bg-gradient-to-r from-transparent via-emerald-300 to-transparent"
            style={{ marginTop: -0.5, left: "-15%" }}
          />
        )}
        <p className="absolute inset-0 flex items-center justify-center text-xs text-emerald-600/70">
          Your email → repeat-purchase flow will light up here
        </p>
      </div>
    </div>
  );
}

// Lightweight prefers-reduced-motion hook (replaces the framer-motion dependency).
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduce(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduce;
}

function SupportStat({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
      <Icon className="w-4 h-4 text-emerald-600 shrink-0" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-gray-900 leading-tight">{value}</p>
        <p className="text-xs text-gray-500 leading-tight">{label}</p>
      </div>
    </div>
  );
}

function HeroSkeleton() {
  return (
    <Card className="overflow-hidden border-emerald-100">
      <CardContent className="p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-56 bg-gray-200 rounded" />
          <div className="h-10 w-40 bg-gray-200 rounded" />
          <div className="h-3 w-80 bg-gray-100 rounded" />
        </div>
      </CardContent>
    </Card>
  );
}

// Free plan / analytics feature not available — subtle nudge, no fake numbers.
function HeroGated() {
  return (
    <Card className="border-dashed">
      <CardContent className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-gray-700">
            <DollarSign className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium">Revenue Attribution</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            See how much revenue comes from customers who purchased after your emails.
          </p>
        </div>
        <Link
          to="/analytics?tab=attribution"
          className="text-sm font-medium text-emerald-700 hover:text-emerald-800 whitespace-nowrap"
        >
          Available on Basic &amp; Pro <ArrowRight className="w-4 h-4 inline" />
        </Link>
      </CardContent>
    </Card>
  );
}
